import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  normalizeVisibleOAPColumns,
  officeAccountingAllFieldsList,
  officeAccountingDefaultConfiguration,
  OfficeAccountingColumnConfig,
} from "@/src/constants/officeAccountingTableConfig";
import { IOfficeAccountingTablePayload, IOfficeAccountingTableRow } from "@/src/types/officeAccounting";
import { getOfficeAccountingRowId } from "@/src/utils/officeAccountingMappers";
import {
  computeOfficeAccountingDebtBalanceUsd,
  computeOfficeAccountingTotalAmountUsd,
  computeOfficeAccountingBalanceMissedClassesEur,
  computeOfficeAccountingBalanceNostrificationsEur,
  getOfficeAccountingTotalPaidUsdRaw,
  getOfficeAccountingPaymentFactEurPaidByInvoice,
  isOfficeAccountingTotalAmountUsdSourceColumn,
  PAYMENT_FACT_EUR_INVOICE,
} from "@/src/utils/officeAccountingTotalAmountUsd";
import {
  resolveBalanceStatusUsdFromAmounts,
  resolveStatusMissedClassesFromAmounts,
  resolveStatusNostrificationFromAmounts,
} from "@/src/constants/officeAccountingTableConfig";
import { formatTuitionFeeDisplay } from "@/src/components/CustomUtils/CustomCalculatePopUp/CustomCalculatePopUp";
import { getUserConfigFromStorage } from "@/src/utils/userConfigStorage";

interface OfficeAccountingTableState {
  visibleColumns: OfficeAccountingColumnConfig[];
  hiddenColumns: OfficeAccountingColumnConfig[];
  data: IOfficeAccountingTableRow[];
  selectedRows: number[];
}

const userConfig = getUserConfigFromStorage();
const initialVisibleColumns = normalizeVisibleOAPColumns(
  userConfig?.OAPConfig?.OAPTable ?? officeAccountingDefaultConfiguration,
);

const getVisibleColumnIdSet = (columns: OfficeAccountingColumnConfig[]): Set<string> =>
  new Set(columns.map((col) => col.id));

const applyDerivedEurPaymentFieldsToRow = (row: IOfficeAccountingTableRow) => {
  const paidMissedClasses = getOfficeAccountingPaymentFactEurPaidByInvoice(
    row,
    PAYMENT_FACT_EUR_INVOICE.MISSED_CLASSES,
  );
  const paidNotification = getOfficeAccountingPaymentFactEurPaidByInvoice(
    row,
    PAYMENT_FACT_EUR_INVOICE.NOTIFICATION,
  );

  row.balanceMissedClassesEur = computeOfficeAccountingBalanceMissedClassesEur(row);
  row.statusMissedClasses = resolveStatusMissedClassesFromAmounts(
    row.totalMissedClassesEur,
    paidMissedClasses,
  );
  row.balanceNostrificationsEur = computeOfficeAccountingBalanceNostrificationsEur(row);
  row.statusNostrification = resolveStatusNostrificationFromAmounts(
    row.totalNostrificationsEur,
    paidNotification,
  );
};

const applyDerivedUsdFieldsToRow = (row: IOfficeAccountingTableRow, visibleIds: Set<string>) => {
  row.totalAmountUsd = computeOfficeAccountingTotalAmountUsd(row, visibleIds);
  row.totalPaidUsd = getOfficeAccountingTotalPaidUsdRaw(row);
  row.debtBalanceUsd = computeOfficeAccountingDebtBalanceUsd(row);
  row.balanceStatusUsd = resolveBalanceStatusUsdFromAmounts(row.totalAmountUsd, row.totalPaidUsd);
  applyDerivedEurPaymentFieldsToRow(row);
};

const recalculateDerivedUsdAmounts = (state: OfficeAccountingTableState) => {
  const visibleIds = getVisibleColumnIdSet(state.visibleColumns);
  for (const row of state.data) {
    applyDerivedUsdFieldsToRow(row, visibleIds);
  }
};

const recalculateDebtBalanceUsdForRow = (row: IOfficeAccountingTableRow, visibleIds?: Set<string>) => {
  if (visibleIds) {
    row.totalAmountUsd = computeOfficeAccountingTotalAmountUsd(row, visibleIds);
  }
  row.totalPaidUsd = getOfficeAccountingTotalPaidUsdRaw(row);
  row.debtBalanceUsd = computeOfficeAccountingDebtBalanceUsd(row);
  row.balanceStatusUsd = resolveBalanceStatusUsdFromAmounts(row.totalAmountUsd, row.totalPaidUsd);
};

const initialState: OfficeAccountingTableState = {
  visibleColumns: initialVisibleColumns,
  hiddenColumns: officeAccountingAllFieldsList.filter(
    (col) => !initialVisibleColumns.some((visibleCol) => visibleCol.id === col.id),
  ),
  data: [],
  selectedRows: [],
};

const officeAccountingTableSlice = createSlice({
  name: "officeAccountingTable",
  initialState,
  reducers: {
    setData(state, action: PayloadAction<IOfficeAccountingTablePayload>) {
      state.data = action.payload.data;
      recalculateDerivedUsdAmounts(state);
    },
    toggleColumnVisibility(state, action: PayloadAction<string>) {
      const colId = action.payload;
      const isVisible = state.visibleColumns.some((col) => col.id === colId);
      if (isVisible) {
        const col = state.visibleColumns.find((col) => col.id === colId)!;
        state.visibleColumns = state.visibleColumns.filter((col) => col.id !== colId);
        state.hiddenColumns.push({ title: col.title, id: col.id });
      } else {
        let col = state.hiddenColumns.find((col) => col.id === colId);
        if (!col) col = officeAccountingAllFieldsList.find((c) => c.id === colId)!;
        state.hiddenColumns = state.hiddenColumns.filter((c) => c.id !== colId);
        state.visibleColumns.push({ title: col.title, id: col.id, width: col.width ?? 5 });
      }
      if (isOfficeAccountingTotalAmountUsdSourceColumn(colId)) {
        recalculateDerivedUsdAmounts(state);
      }
    },
    setColumnWidth(state, action: PayloadAction<{ id: string; width: number }>) {
      const { id, width } = action.payload;
      const col = state.visibleColumns.find((c) => c.id === id);
      if (col) col.width = width;
    },
    setRowFieldValue(
      state,
      action: PayloadAction<{ studentInfoId: number; field: keyof IOfficeAccountingTableRow; value: string }>,
    ) {
      const { studentInfoId, field, value } = action.payload;
      const row = state.data.find((r) => getOfficeAccountingRowId(r) === studentInfoId);
      if (!row) return;

      (row[field] as string) = value;
      if (isOfficeAccountingTotalAmountUsdSourceColumn(String(field)) || field === "currentSemester") {
        recalculateDebtBalanceUsdForRow(row, getVisibleColumnIdSet(state.visibleColumns));
      } else if (field === "paymentFactUsd") {
        recalculateDebtBalanceUsdForRow(row);
      } else if (field === "paymentForMissedClasses") {
        row.totalMissedClassesEur = formatTuitionFeeDisplay(value);
        applyDerivedEurPaymentFieldsToRow(row);
      } else if (field === "nostrification") {
        row.totalNostrificationsEur = formatTuitionFeeDisplay(value);
        applyDerivedEurPaymentFieldsToRow(row);
      } else if (field === "paymentFactEur") {
        applyDerivedEurPaymentFieldsToRow(row);
      }
    },
    toggleRowSelection(state, action: PayloadAction<number>) {
      const id = action.payload;
      if (state.selectedRows.includes(id)) {
        state.selectedRows = state.selectedRows.filter((rowId) => rowId !== id);
      } else {
        state.selectedRows.push(id);
      }
    },
    toggleAllRows(state) {
      if (state.selectedRows.length === state.data.length) {
        state.selectedRows = [];
      } else {
        state.selectedRows = state.data.map((row) => getOfficeAccountingRowId(row));
      }
    },
    showAllColumns(state) {
      state.visibleColumns = [...officeAccountingAllFieldsList];
      state.hiddenColumns = [];
      recalculateDerivedUsdAmounts(state);
    },
    hideAllColumns(state) {
      state.hiddenColumns = [...officeAccountingAllFieldsList];
      state.visibleColumns = [];
      recalculateDerivedUsdAmounts(state);
    },
    setVisibleColumns(state, action: PayloadAction<OfficeAccountingColumnConfig[]>) {
      const normalized = normalizeVisibleOAPColumns(action.payload);
      state.visibleColumns = normalized;
      state.hiddenColumns = officeAccountingAllFieldsList.filter(
        (col) => !normalized.some((visibleCol) => visibleCol.id === col.id),
      );
      recalculateDerivedUsdAmounts(state);
    },
  },
});

export const {
  setData,
  toggleColumnVisibility,
  setColumnWidth,
  setRowFieldValue,
  toggleRowSelection,
  toggleAllRows,
  showAllColumns,
  hideAllColumns,
  setVisibleColumns,
} = officeAccountingTableSlice.actions;

export default officeAccountingTableSlice.reducer;
